import * as React from 'react';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { XIcon } from 'lucide-react';
import {
  BottomSheetPopup,
  BottomSheetRoot,
  type PhoneLayout,
} from '@/components/ui/phone/BottomSheet';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';

/*
  On a phone every dialog is a bottom sheet (spec 261): it rises from the edge
  the thumb is at, and is swiped back down. The root becomes Base UI's drawer,
  which is its dialog with the swipe added, so the parts below (Trigger, Close,
  Title, Description) are the same parts in either. `DialogContent` takes a
  `phoneLayout` for a dialog that is a place rather than a question (`full`).
*/

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  const phone = usePhoneWidth();
  if (phone) {
    return (
      <BottomSheetRoot
        {...(props as React.ComponentProps<typeof BottomSheetRoot>)}
      />
    );
  }
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 isolate z-50 bg-black/10 duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)] data-closed:duration-[var(--motion-duration-fast)] data-closed:ease-[var(--motion-ease-exit)] supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  phoneLayout = 'sheet',
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  /** How it sits on a phone (spec 261); a bottom sheet unless said otherwise. */
  phoneLayout?: PhoneLayout;
}) {
  const phone = usePhoneWidth();
  if (phone && phoneLayout !== 'keep') {
    return (
      <BottomSheetPopup
        {...(props as React.ComponentProps<typeof BottomSheetPopup>)}
        layout={phoneLayout}
        slot="dialog-content"
        closeClassName="top-2 right-2"
        showCloseButton={showCloseButton}
        // `content-start`: at full height a grid stretches its rows to fill
        // it, which drew the palette's one-line field 180px tall.
        className={cn('grid content-start gap-4 p-4 pt-6', className)}
      >
        {children}
      </BottomSheetPopup>
    );
  }
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          'fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground shadow-floating ring-1 ring-foreground/10 duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)] data-closed:duration-[var(--motion-duration-fast)] data-closed:ease-[var(--motion-ease-exit)] outline-none sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <Button
                variant="ghost"
                className="absolute top-2 right-2"
                size="icon-sm"
              />
            }
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  flush = false,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  /** Full-width footer bar for split layouts (header/body/footer with p-0 content). */
  flush?: boolean;
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex shrink-0 flex-col-reverse gap-2 border-t border-border sm:flex-row sm:justify-end sm:gap-2.5',
        // On a phone's bottom sheet (spec 261) the footer stays at the sheet's
        // bottom edge while a long form scrolls under it, opaque so the form
        // does not show through, and clear of the home indicator.
        'max-md:sticky max-md:bottom-0 max-md:z-10 max-md:pb-[max(1rem,env(safe-area-inset-bottom))]',
        flush
          ? 'bg-muted/30 px-5 py-3.5 [&_[data-slot=button]]:min-w-[5.5rem] [&_[data-slot=button]]:px-4'
          : '-mx-4 -mb-4 rounded-b-xl bg-muted/50 p-4',
        // The same tint, mixed onto the sheet's own surface so it is opaque.
        flush
          ? 'max-md:bg-[color-mix(in_oklab,var(--color-muted)_30%,var(--color-popover))]'
          : 'max-md:rounded-b-none max-md:bg-[color-mix(in_oklab,var(--color-muted)_50%,var(--color-popover))]',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        'font-heading text-base leading-none font-medium',
        className,
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
